import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Colors } from '../../styles/colors';

interface SteeringWheelProps {
  size?: number;
  color?: string;
  rotating?: boolean;
  rotationSpeed?: number; // seconds per full rotation
  className?: string;
}

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Container = styled.div<{ $size: number; $rotating: boolean; $rotationSpeed: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  ${props =>
    props.$rotating &&
    css`
      animation: ${rotate} ${props.$rotationSpeed}s linear infinite;
    `}
`;

const SteeringWheel: React.FC<SteeringWheelProps> = ({
  size = 200,
  color = Colors.PrimaryRed,
  rotating = false,
  rotationSpeed = 2,
  className,
}) => {
  return (
    <Container
      $size={size}
      $rotating={rotating}
      $rotationSpeed={rotationSpeed}
      className={className}
    >
      <svg width={size} height={size} viewBox="0 0 1520 1520" preserveAspectRatio="xMidYMid meet">
        <g>
          {/* Steering wheel body */}
          <g transform="translate(273, 220)">
            <path
              d="m0 0h30l5 3 10 1 6 5v2l6 1 6 7 3 5 7 8 5 5 5 6v8l3 1 1 3 1 24 2 1v4l-2 1-1 25-1 2h-2l-1 9-6 11-5 5-2 3h-2l-1 6-11 20-7 14-6 9-15 30-3 9-9 19-3 10-4 12-3 11-2 2-2 14-2 4-2 12-2 3-2 12-2 4-2 12-2 5-1 12-3 6v32l3 7 2 6 4 5 10 6 7 5 8 2 5 2h334l9-3 13-7 3-3 2-5 5-5 6-3 10-9 6-3 6-5 8-4 8-1 8-7 11-1 5-3 5-2 2-2 43-1 1-3h9l3 3 22 1 2 3 16 1 19 9 16 9 9 6 11 6 5 5 7 6 6 7 2 1v2l4 2 10 6 73 1 5 3 115 1 2 3h16l4-2 6 1 2-2 80-1 7-2 1-1 11-1 19-9 8-7 4-5 4-10v-27l-2-6-1-2-1-17-7-15-1-17-7-20-2-11-5-10-4-12-5-11-3-9-8-16-2-12-25-50-9-10-17-34-1-11-3-5-1-11-3-4v-12l3-5 1-14 3-6 2-8 4-8 9-10 3-6 5-6 7-4 13-7 13-2 2-2h29l5 3 9 2 9 3 11 6 5 5 3 2v2l3 1 5 7 3 5 9 11 3 5 5 7 3 5 9 10 4 5 1 7 7 9 4 7 7 10 5 8 2 7 5 6 8 16 5 11 2 7 5 6 5 12 5 13 6 15 3 10 8 24 6 14 2 6 3 11 2 3 2 17 3 7 4 14 1 15 2 3 2 13 2 3 2 18 2 1 1 6v21l3 1 1 4 1 23 3 3 1 73 3 3v5l-3 2v2l3 3-3 3v6l3 3-3 3v10l3 2-1 3h-3v60l-1 4-2 1-1 32-2 7-1 2-1 17-1 5h-2l-1 16-1 4h-2l-1 15-2 6-1 1-2 14h-2l-1 16-2 5-1 4-2 11h-2l-1 12-3 7-3 9-5 12-5 15-4 10-7 14-6 20-5 9-6 9-7 15-3 8-8 17-6 10-3 4-7 14-8 15-7 8h-2l-2 7-4 5-7 6-1 2h-2l-2 7-4 5h-2l-2 7-4 5-4 4h-2l-2 7-4 8-7 3-7 6-7 4-5 4-8 1-1 2-9 1h-38l-3-3-10-2-2-2-7-3-5-5-5-2-9-9-2-5-10-11-2-9-2-3-1-14h-2l1-5 1-1 1-32 11-21 7-14 6-10 7-10 7-11 5-7 7-14 5-9 4-10 1-10 6-7 3-10 5-14 5-11 3-9 5-9 5-14 3-9 4-16 2-10 2-6 2-14 2-5 2-16 3-8 3-9v-32l-6-12-8-8-3-2-13-2-5-2-15-2-5-2h-55l-3 3-222 1-6 11-3 4h-2l-2 7-5 9-3 4h-2l-2 4-4 4h-2l-2 7-21 21-5 2-12 11-8 8-1 26-3 3-1 5 3 1 1 2v12l-3 3-1 10 4 2-1 3-2 1v249l3 7 2 6 7 8 8 7 1 1 10 2v2h15l5-2 1-1 14-2 2-2 11-1 5-3 10-2 3-2 17-2 2-2 14-2 3-2h36l6 3 9 3 10 6 10 7 5 5 5 8 5 7 4 10 2 6 2 7v44l-1 2h-2l-1 10-7 8-2 5-7 7-5 6-6 4-10 4-6 2-19 9-10 3-5 2-10 2-11 5-14 3-5 2-9 3-15 2-4 2-16 2-4 2-17 2-3 2-28 2h-7v2l-32 2h-7l-1 2-16 1h-56l-8-1-2-2-33-1-15-1-16-3-38-6-36-8-19-5-14-3-21-7-19-9-17-9-14-7-6-5-6-3-11-11-9-18v-51l3-5 2-8 4-8 11-12 10-9 7-4 5-4 12-1 3-3h38l1 3 18 1 6 3 12 2 4 2 8 2 4 2 8 2 4 2h37l10-5 8-7 6-10 2-5v-307l-3-3-1-12-7-8-2-5-7-3-8-5-22-22-7-14-6-8-6-12-6-8-1-5-10-1-2-2-295-1-2 4-24 1-11 6-13 13-1 2-1 17-3 2v2l3 2v22l3 1 1 6 1 17 2 2 2 13 2 3 2 17 2 3 2 13 3 7 2 6 9 19 3 10 3 9 9 19 2 5 2 6 5 6 2 13 5 9 6 9 4 8 2 7 9 11 9 17 9 19 6 7 2 10 3 4 1 16 3 2-1 3-2 1v6l3 2-1 3-2 1-1 21-3 4-2 5-7 7-1 2h-2l-2 7-5 5-6 2-14 14-9 1-2 2-18 2-1 2-14 1-4-3-12-1-4-3-8-1-4-3-5-2-8-7-7-8-5-8-6-9-7-10-3-5-9-11-3-5-9-11-3-5-6-9-7-10-6-9-9-16-5-8-4-11-19-38-2-8-1-6-6-9-3-8-5-8-5-14-4-10-6-14-4-15-4-16-2-12-2-8-16-80-1-3-1-17-2-8-1-2-1-37h-2l-1-2v-132l3-4 1-51 3-5 1-15 3-7 2-15 2-5 1-16 3-8 1-13 3-7 4-13 1-12 3-7 2-15 2-5 1-9 3-5 4-11 3-8 12-24 3-10 5-10 3-10 5-6 3-10 20-40 10-11 1-8 7-7 4-6 4-7 2-5 7-7 7-9 6-9 3-5 9-11 5-8 9-10 7-6 8-4 9-2 2-2 11-1zm485 456-16 8-11 6-6 5-7 4-7 7v2h-2l-3 7-8 16-2 3-2 13-2 5v31l3 1 1 10 3 7 2 6 4 8 6 8 2 5 10 6 9 5 9 6h3v2l5 2h3v2l2 1 12 1 3 2h3l1-2h2l2 3h17l4-3 5-2 2-2 10-2 9-5 9-6 10-8 9-10 3-7 8-16 3-10 2-6v-33l-3-5-2-9-2-3-1-7-7-9-4-7-7-7-7-4-9-7-21-10-3-2z"
              fill={color}
            />
          </g>
        </g>
      </svg>
    </Container>
  );
};

export default SteeringWheel;
